import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Mail, MapPin, MessageCircle, Phone, UserPlus } from 'lucide-react';
import { formatPhone, usePublicSettings } from '@/layouts/PublicShell';
import { PageHero } from './shared';

/**
 * Contact page.
 *
 * No message form. UNIFEMGA has no ticketing system behind one, and a form that
 * posts into nothing is worse than a telephone number — it looks answered.
 * Members with an account should use the portal, where their message is
 * attached to their file.
 */
export default function Contact() {
  const org = usePublicSettings();

  const channels = [
    {
      icon: Phone,
      label: 'Telephone',
      value: formatPhone(org['org.phone']),
      href: `tel:${org['org.phone']}`,
      note: 'The fastest way to reach us about a loan or a guarantor call.',
    },
    {
      icon: Mail,
      label: 'Email',
      value: org['org.email'],
      href: `mailto:${org['org.email']}`,
      note: 'Best for documents and anything you want a written record of.',
    },
    {
      icon: MapPin,
      label: 'In person',
      value: org['org.address'],
      note: 'Come to the secretariat if you would rather speak to someone face to face.',
    },
  ];

  return (
    <>
      <PageHero
        eyebrow="Contact us"
        title="Talk to a person."
        description="A loan question, a guarantor confirmation, or trouble with a repayment — there is someone to speak to about all of it."
      />

      <section className="py-14 md:py-20">
        <div className="container">
          <div className="grid gap-6 lg:grid-cols-3">
            {channels.map(({ icon: Icon, label, value, href, note }) => (
              <div
                key={label}
                className="flex flex-col rounded-2xl border border-border bg-card p-7 transition-all hover:-translate-y-1 hover:shadow-floating"
              >
                <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon size={20} />
                </span>
                <p className="text-xs font-black uppercase tracking-wider text-foreground-secondary">
                  {label}
                </p>
                {href ? (
                  <a
                    href={href}
                    className="mt-1.5 break-words font-heading text-lg font-bold text-foreground transition-colors hover:text-primary"
                  >
                    {value}
                  </a>
                ) : (
                  <p className="mt-1.5 font-heading text-lg font-bold leading-snug text-foreground">
                    {value}
                  </p>
                )}
                <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-secondary">
                  {note}
                </p>
              </div>
            ))}
          </div>

          {/* Route people to the right place rather than a generic inbox. */}
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-border bg-background-alt p-7 sm:p-8">
              <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <MessageCircle size={20} />
              </span>
              <h2 className="mb-2.5 font-heading text-lg font-bold text-foreground">
                Already a member?
              </h2>
              <p className="mb-6 text-sm leading-relaxed text-foreground-secondary">
                Sign in first. Anything you raise from your portal is attached to your file, so
                whoever picks it up can already see your loans, your applications and your
                repayment history.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 font-bold text-primary hover:underline"
              >
                Sign in to your portal <ArrowRight size={16} />
              </Link>
            </div>

            <div className="rounded-2xl border border-border bg-background-alt p-7 sm:p-8">
              <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <UserPlus size={20} />
              </span>
              <h2 className="mb-2.5 font-heading text-lg font-bold text-foreground">
                Not registered yet?
              </h2>
              <p className="mb-6 text-sm leading-relaxed text-foreground-secondary">
                You will need your UNIFEMGA membership number. If you do not know it, telephone the
                secretariat and they will look it up for you.
              </p>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 font-bold text-primary hover:underline"
              >
                Create an account <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* Expectation-setting, not a false promise of 24/7 support. */}
          <div className="mt-12 flex flex-col gap-4 rounded-2xl border border-border bg-card p-6 sm:flex-row sm:items-center sm:gap-6 sm:p-7">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground-secondary">
              <Clock size={20} />
            </span>
            <div>
              <h3 className="font-heading text-base font-bold text-foreground">
                If you are calling about a guarantor
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-foreground-secondary">
                Every guarantor on an application is telephoned by an officer before that
                application can be approved. If someone you named has not been reached, calling us
                with a better number is the quickest way to unblock it.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
